// AI 토큰 사용량 관리자 API 클라이언트 — ai-orchestration의 /api/admin/usage/* (게이트웨이 경유)
import { GW_ORIGIN } from 'yukimushi-component-fe';
import { authorizedFetch } from './auth';

/**
 * 게이트웨이(/chat-api)를 그대로 탄다 — 채팅 API와 같은 서버이고,
 * 관리자 판정에 필요한 X-User-* 헤더는 게이트웨이가 주입한다.
 * 프론트가 그 헤더를 만들지 않으며, 관리자 역할이 없으면 403이 온다.
 *
 * 관리자 API에는 CORS 설정이 없어 절대 URL로 직접 부르면 브라우저가 막는다.
 * 그래서 게이트웨이가 서빙하는 origin에서만 공용 정책(GW_ORIGIN)을 따르고,
 * 로컬 개발에서는 상대경로로 보내 dev 서버 프록시(setupProxy의 /chat-api →
 * SSH 터널 18080 → 게이트웨이 8080)를 타게 한다. 즉 로컬에서는 터널이 떠 있어야 한다.
 */
const SERVED_BY_GATEWAY = window.location.hostname.indexOf('gennolab.co.jp') >= 0;
const USAGE_API_BASE = `${SERVED_BY_GATEWAY ? GW_ORIGIN : ''}/chat-api/api/admin/usage`;

/** 조회 기간 — 서버가 KST(zone) 기준으로 자르고 to는 그날을 포함한다 */
export interface UsageRange {
  from: string;
  to: string;
  zone: string;
}

export interface UsageTotals {
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  /** LLM 호출 수 (채팅 수가 아니다 — 채팅 1회가 보통 3~4 호출) */
  requestCount: number;
  userCount: number;
}

export interface UsageDaily {
  day: string;
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  requestCount: number;
}

export interface UsageModel {
  /** 'chat'(대화 생성) 또는 'embedding'(문서 검색용 질의 벡터화) */
  operation: string;
  model: string;
  totalTokens: number;
  requestCount: number;
}

export interface UsageUser {
  userId: string;
  /** 사번 — 아직 채팅 이력이 없는 사용자는 null */
  username: string | null;
  deptCode: string | null;
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  requestCount: number;
  /** ISO 8601(+09:00). 상세 응답에서는 항상 null이라 목록에서 받은 값을 쓴다 */
  lastUsedAt: string | null;
}

export interface UsageSummary {
  range: UsageRange;
  totals: UsageTotals;
  daily: UsageDaily[];
  models: UsageModel[];
}

export interface UsageUserList {
  range: UsageRange;
  users: UsageUser[];
}

export interface UsageUserDetail {
  range: UsageRange;
  user: UsageUser;
  daily: UsageDaily[];
  models: UsageModel[];
}

/** HTTP 상태를 실은 에러 — 호출부가 403(권한 없음)을 다른 실패와 갈라 처리한다 */
export interface UsageError extends Error {
  status: number;
}

/* target이 es5라 Error를 상속하면 instanceof가 깨진다(프로토타입 체인 소실).
   상태값만 얹은 평범한 Error로 충분하므로 서브클래스를 만들지 않는다.
   status 0은 응답을 받지 못한 경우(네트워크·프록시 단절)다. */
function usageError(status: number, message: string): UsageError {
  const err = new Error(message) as UsageError;
  err.status = status;
  return err;
}

function httpFailure(status: number): UsageError {
  if (status === 403) return usageError(403, '관리자 권한이 없습니다');
  return usageError(status, `사용량을 불러오지 못했습니다 (HTTP ${status})`);
}

async function getJson<T>(path: string, params: Record<string, string | number | undefined>): Promise<T> {
  const query = new URLSearchParams();
  Object.keys(params).forEach((key) => {
    const value = params[key];
    if (value !== undefined && value !== '') query.append(key, String(value));
  });
  const qs = query.toString();

  // 호출부가 상태값 하나로 분기할 수 있도록 네트워크 실패도 같은 형태로 감싼다
  let res: Response;
  try {
    res = await authorizedFetch(`${USAGE_API_BASE}${path}${qs ? `?${qs}` : ''}`);
  } catch {
    throw usageError(0, '서버에 연결하지 못했습니다');
  }
  if (!res.ok) throw httpFailure(res.status);
  try {
    return await res.json();
  } catch {
    throw usageError(res.status, '응답을 해석하지 못했습니다');
  }
}

/** 조직 전체 요약 — from/to 생략 시 서버가 최근 30일로 잡는다 */
export function fetchUsageSummary(from?: string, to?: string): Promise<UsageSummary> {
  return getJson<UsageSummary>('/summary', { from, to });
}

/** 사용자 랭킹 — 총 토큰 내림차순 고정. limit 최대 500 */
export function fetchUsageUsers(from?: string, to?: string, limit = 50): Promise<UsageUserList> {
  return getJson<UsageUserList>('/users', { from, to, limit });
}

/** 사용자 상세 — user.lastUsedAt은 항상 null이므로 목록에서 받은 값을 써야 한다 */
export function fetchUsageUserDetail(userId: string, from?: string, to?: string): Promise<UsageUserDetail> {
  return getJson<UsageUserDetail>(`/users/${encodeURIComponent(userId)}`, { from, to });
}

/**
 * 사번·부서 부분 일치 검색 (최대 50건) — 신원 조회 전용이다.
 * 응답의 토큰 값은 전부 0이므로 수치로 쓰면 안 된다. 수치는 상세 API로 다시 받는다.
 */
export function searchUsageUsers(keyword: string): Promise<UsageUser[]> {
  return getJson<UsageUser[]>('/user-search', { keyword });
}
